import * as React from 'react';
import type { SearchResults } from 'state/ui/search';
import Transition from 'react-transition-group/Transition';
import type { TransitionState } from 'view/styles/transitions';
import { ResultLoading, ResultError, ResultSome, ResultNone, Container, MODAL_FADE_TRANSITION_MS } from './styled';
import { TRACK_EVENT_TYPE, OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { SearchResultLoading } from '../loading';
import { DEFAULT_MAX_KB_RESULTS_IN_DETAILS_PAGE } from '@atlassian/help-center-common-util/search-providers/search-providers';

export interface SearchResultProps {
    term: string;
    queryTime: number;
    result: SearchResults;
    isSearching: boolean;
    hasError: boolean;
    searchStartTime?: number;
    className?: string;
    isSearchingPortals?: boolean;
    isSearchingRequestTypes?: boolean;
    isSearchingKb?: boolean;
}

interface Props extends SearchResultProps {
    isShown: boolean;
    autoFocusOnFirstItem?: boolean;
    itemsFocusable?: boolean;
    portalId?: string;
}

const availableSectionsCount = (result: SearchResults) =>
    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
    // @ts-ignore TS(7053) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    Object.keys(result).filter((key) => result[key].length > 0 || result[key].totalSize > 0).length;

const SearchResult: React.FC<Props & WithAnalyticsEventsProps> = ({
    term,
    queryTime,
    result,
    hasError,
    isSearching,
    isSearchingPortals,
    isSearchingRequestTypes,
    isSearchingKb,
    autoFocusOnFirstItem,
    itemsFocusable,
    isShown,
    createAnalyticsEvent,
    searchStartTime,
    portalId,
    ...props
}) => {
    const isSearchMounted = React.useRef(false);
    const isSearchInProgress = isSearching || isSearchingPortals || isSearchingKb || isSearchingRequestTypes;
    const [firstResultDisplayEventFired, setFirstResultDisplayEventFired] = React.useState(false);

    const sendSearchEvent = (searchType: string) => {
        const searchResultCount = availableSectionsCount(result);
        const isEmpty = !searchResultCount;
        const { portals, knowledgeBase, requestTypes } = result;
        const resultCount = isEmpty ? null : portals.length + knowledgeBase.articles.length + requestTypes.length;
        const sectionCount = isEmpty ? null : searchResultCount;

        createAnalyticsEvent &&
            createAnalyticsEvent({
                action: 'shown',
                analyticsType: TRACK_EVENT_TYPE,
                actionSubjectId: 'helpCenter',
                actionSubject: 'searchResults',
                type: 'ui',
                attributes: {
                    isEmpty,
                    resultCount,
                    sectionCount,
                    type: searchType,
                    queryLength: term.length,
                    portalCount: isEmpty ? null : portals.length,
                    articleCount: isEmpty ? null : knowledgeBase.articles.length,
                    totalArticleCount: isEmpty
                        ? null
                        : Math.min(knowledgeBase.totalSize, DEFAULT_MAX_KB_RESULTS_IN_DETAILS_PAGE),
                    requestTypeCount: isEmpty ? null : requestTypes.length,
                    requestTypeSearchMetaData: isEmpty
                        ? null
                        : requestTypes.map((requestType) => requestType.searchMetaData),
                },
                source: 'portalSearchModal',
            }).fire();
    };
    React.useEffect(() => {
        if (!isSearchInProgress && isSearchMounted.current) {
            sendSearchEvent('postQuery');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createAnalyticsEvent, result, isSearchInProgress]);

    React.useEffect(() => {
        if (
            (availableSectionsCount(result) || !isSearchInProgress) &&
            !firstResultDisplayEventFired &&
            !!searchStartTime
        ) {
            setFirstResultDisplayEventFired(true);
            createAnalyticsEvent &&
                createAnalyticsEvent({
                    action: 'firstSearchResultsShown',
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
                    type: 'ui',
                    attributes: {
                        timeTaken: performance.now() - searchStartTime,
                    },
                    source: 'portalSearchModal',
                }).fire();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createAnalyticsEvent, result, isSearchInProgress]);

    React.useEffect(() => {
        setFirstResultDisplayEventFired(false);
    }, [term]);

    React.useEffect(() => {
        if (isShown && !isSearchInProgress) {
            sendSearchEvent('cachedResults');
        }
        isSearchMounted.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const noResultSection = (state: TransitionState) =>
        isSearchingPortals || isSearchingRequestTypes ? <SearchResultLoading /> : <ResultNone state={state} />;

    return (
        <Container {...props}>
            <Transition in={isSearching} timeout={MODAL_FADE_TRANSITION_MS} mountOnEnter unmountOnExit>
                {(state: TransitionState) => <ResultLoading state={state} />}
            </Transition>

            <Transition in={!isSearching} timeout={MODAL_FADE_TRANSITION_MS} mountOnEnter unmountOnExit>
                {(state: TransitionState) => {
                    if (hasError) {
                        return <ResultError state={state} />;
                    }

                    return availableSectionsCount(result) ? (
                        <ResultSome
                            portalId={portalId}
                            result={result}
                            term={term}
                            queryTime={queryTime}
                            state={state}
                            autoFocusOnFirstItem={autoFocusOnFirstItem}
                            itemsFocusable={itemsFocusable}
                            isSearchingPortals={isSearchingPortals}
                            isSearchingRequestTypes={isSearchingRequestTypes}
                            isSearchingKb={isSearchingKb}
                        />
                    ) : (
                        noResultSection(state)
                    );
                }}
            </Transition>
        </Container>
    );
};

export default withAnalyticsEvents()(SearchResult);
